/**
 * « Ce que ce virement dit » : les factures qu'il cite, et ce qu'elles
 * réclament réellement.
 *
 * ## Le cas qui a fait écrire ce panneau
 *
 * Le 18 septembre 2026, devant un virement de 180,00 € dont 70,00 € restaient
 * à placer :
 *
 *   « il est très compliqué pour l'utilisateur de comprendre ce qu'il se
 *     passe, nous avons bien les trois factures qui sont reconnues mais il y a
 *     un montant trop perçu... sans doute dû à des notes de crédit mais comme
 *     l'utilisateur ne les voit pas, il ne peut pas juger si c'est exact ou
 *     pas, s'il doit mettre hors périmètre ou mettre en provision le reste »
 *
 * Il a fallu interroger la base à la main pour comprendre. La réponse tenait
 * en trois faits, dont aucun n'était affiché :
 *
 * 1. Le virement cite **cinq** factures, pas trois : les deux dernières ne
 *    sont que dans le libellé complet du relevé, la communication bancaire
 *    s'arrêtant à 53 caractères. Elles sont impayées, 40,00 € en tout.
 * 2. Une des cinq appartient à **une autre famille**.
 * 3. Les 30,00 € restants sont **exactement** la note de crédit portée par
 *    cette facture-là : le parent a payé le montant d'origine.
 *
 * ## Ce que ce panneau garantit
 *
 * Il lit les références avec `allLegacyRefs`, **la fonction du rapprochement
 * automatique**. Un panneau qui lirait autrement montrerait une référence que
 * la machine n'a jamais vue, ou tairait celle sur laquelle elle a agi : il
 * expliquerait un écran qui n'existe pas.
 *
 * Et il ne décide rien. Le bouton de placement écrit **ce qu'il annonce**, sur
 * les seules factures encore dues, et il faut cliquer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ChevronDown, ChevronRight, Landmark, Loader2, Receipt, Wallet,
} from 'lucide-react';
import clsx from 'clsx';
import {
  lireLeVirement, partsProposees, referencesCitees, type FactureCitee,
} from '../../../lib/payments/facturesCitees';
import { lireFacturesCitees } from '../../../lib/payments/situation.supabase';
import { allocatePayment, type PendingPayment } from '../../../lib/payments/actions.supabase';
import { messageErreur } from '../../../lib/payments/erreur';
import { ETATS, type Settlement } from '../../../lib/factures/types';
import CompteDeLaFamille from './CompteDeLaFamille';
import LienFacture from './LienFacture';
import { euros } from './money';

/** Où en est la lecture du libellé complet du relevé, chez l'appelant. */
export type EtatDuReleve = 'lecture' | 'lu' | 'echec';

export default function FacturesCitees({
  paiement, libelleDuReleve, etatDuReleve, enCours, agir,
}: {
  paiement: PendingPayment;
  /** Le libellé complet du relevé, qui porte souvent la suite tronquée. */
  libelleDuReleve: string | null;
  /**
   * On ATTEND ce libellé avant de chercher. Lire d'abord la communication
   * seule afficherait trois factures, puis cinq une seconde plus tard — et
   * dans l'intervalle, un total faux que quelqu'un peut lire et croire.
   */
  etatDuReleve: EtatDuReleve;
  enCours: boolean;
  agir: (action: () => Promise<void>, message: string) => Promise<void>;
}) {
  const references = useMemo(
    () => (etatDuReleve === 'lecture'
      ? []
      : referencesCitees(paiement.communicationRaw, libelleDuReleve)),
    [paiement.communicationRaw, libelleDuReleve, etatDuReleve],
  );

  const [factures, setFactures] = useState<FactureCitee[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteOuvert, setCompteOuvert] = useState<string | null>(null);

  /*
    `unallocatedCents` est dans les dépendances À DESSEIN. Il change dès qu'une
    écriture a eu lieu sur ce virement, et c'est le seul signal qui dise « les
    soldes affichés ici viennent de bouger ». Sans lui, placer 40,00 € laissait
    le tableau annoncer 40,00 € encore dus — et le geste suivant se décidait
    sur des chiffres périmés.
  */
  const charger = useCallback(async () => {
    if (references.length === 0) { setFactures([]); return; }
    try {
      setFactures(await lireFacturesCitees(references, paiement.id));
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture des factures citées impossible'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [references, paiement.id, paiement.unallocatedCents]);

  useEffect(() => { void charger(); }, [charger]);

  if (etatDuReleve === 'lecture') {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture des factures citées…
      </p>
    );
  }

  // Rien de cité : ce panneau n'aurait rien à dire, et un cadre vide se lit
  // comme une panne. Les suggestions et la recherche manuelle restent là.
  if (references.length === 0) return null;

  if (erreur) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{erreur}</span>
      </p>
    );
  }

  if (factures === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture des factures citées…
      </p>
    );
  }

  const lecture = lireLeVirement(paiement.unallocatedCents, factures);
  const parts = partsProposees(paiement.unallocatedCents, factures);
  const aPlacer = parts.reduce((s, p) => s + p.montantCents, 0);
  const comptes = [...new Map(
    factures.filter((f) => f.userId).map((f) => [f.userId as string, f.famille ?? 'Compte sans nom']),
  )];

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Receipt className="h-3.5 w-3.5" /> Ce que ce virement dit
      </p>

      <p className="mt-2 text-sm text-gray-800">{lecture.phrases.join(' ')}</p>

      {etatDuReleve === 'echec' && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Le libellé complet du relevé n'a pas pu être lu : seules les factures citées dans
            les 53 caractères de la communication sont listées ici. Il peut en manquer.
          </span>
        </p>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 text-left font-medium">Facture citée</th>
              <th className="py-1 text-left font-medium">Famille</th>
              <th className="py-1 text-left font-medium">État</th>
              <th className="py-1 text-right font-medium">Montant</th>
              <th className="py-1 text-right font-medium">Note de crédit</th>
              <th className="py-1 text-right font-medium">Déjà reçu</th>
              <th className="py-1 text-right font-medium">Reste dû</th>
              <th className="py-1 text-right font-medium">Ce virement</th>
            </tr>
          </thead>
          <tbody>
            {factures.map((f) => {
              const etat = f.settlement ? ETATS[f.settlement as Settlement] : undefined;
              const reclame = f.settlement !== 'cancelled' && f.resteCents > 0;
              return (
                <tr key={f.digits} className="border-t border-gray-100 align-top">
                  <td className="py-1.5">
                    {f.invoiceNumber
                      ? <LienFacture invoiceNumber={f.invoiceNumber} pdfUrl={f.pdfUrl} />
                      : <span className="font-mono text-xs text-gray-500">{f.digits}</span>}
                    {/*
                      D'où vient la référence. Deux des cinq factures du cas
                      réel n'étaient QUE dans le libellé du relevé : c'est la
                      raison pour laquelle elles n'ont jamais été rapprochées,
                      et elle doit se lire.
                    */}
                    {f.origine === 'releve' && (
                      <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-900">
                        libellé du relevé
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-xs text-gray-700">
                    {f.famille ?? <span className="text-gray-400">facture inconnue ici</span>}
                  </td>
                  <td className="py-1.5">
                    {etat && (
                      <span className={clsx(
                        'inline-block rounded px-1.5 py-0.5 text-xs font-medium', etat.classe,
                      )}>
                        {etat.label}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-700">
                    {f.invoiceId ? euros(f.factureCents) : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-indigo-800">
                    {f.notesCents > 0 ? `−${euros(f.notesCents)}` : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-700">
                    {f.invoiceId ? euros(f.recuCents) : '—'}
                  </td>
                  <td className={clsx(
                    'py-1.5 text-right tabular-nums',
                    reclame ? 'font-medium text-amber-800' : 'text-gray-500',
                  )}>
                    {f.invoiceId ? euros(f.resteCents) : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-blue-900">
                    {f.deCeVirementCents > 0 ? euros(f.deCeVirementCents) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {parts.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-600">
            {parts.map((p) => `${p.facture.invoiceNumber} ${euros(p.montantCents)}`).join(' · ')}
          </p>
          <button
            disabled={enCours}
            onClick={() => agir(
              () => allocatePayment(paiement.id, parts.map((p) => ({
                target: 'invoice' as const,
                amountCents: p.montantCents,
                invoiceId: p.facture.invoiceId as string,
                note: p.facture.origine === 'releve'
                  ? 'Facture citée dans le libellé complet du relevé'
                  : 'Facture citée dans la communication',
              }))),
              `${euros(aPlacer)} placés sur ${parts.length === 1 ? 'la facture citée' : `${parts.length} factures citées`}`,
            )}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Landmark className="h-4 w-4" />
            Placer {euros(aPlacer)} sur {parts.length === 1 ? 'cette facture' : `ces ${parts.length} factures`}
          </button>
        </div>
      )}

      {comptes.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Comptes concernés
          </p>
          {comptes.map(([userId, nom]) => (
            <div key={userId}>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setCompteOuvert(compteOuvert === userId ? null : userId)}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {compteOuvert === userId
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />}
                  {nom}
                </button>

                {/*
                  La provision, depuis l'endroit où la famille est nommée.
                  Signalé le 18 septembre 2026 sur un virement de 40,00 € dont
                  la facture citée était ANNULÉE : « pourquoi ne puis-je pas le
                  mettre en provision ? La facture a été annulée mais on a quand
                  même reçu les sous. » L'écran nommait la famille juste
                  au-dessus et n'offrait que « hors périmètre » — c'est-à-dire
                  le seul geste FAUX, puisque l'argent est bien arrivé et
                  appartient à cette famille.
                */}
                {lecture.ecartCents > 0 && (
                  <button
                    disabled={enCours}
                    onClick={() => agir(
                      () => allocatePayment(paiement.id, [{
                        target: 'provision' as const,
                        amountCents: lecture.ecartCents,
                        provisionUserId: userId,
                        note: 'Reliquat mis en provision : aucune facture citée ne le réclame',
                      }]),
                      `${euros(lecture.ecartCents)} mis en provision pour ${nom}`,
                    )}
                    className="inline-flex items-center gap-1 rounded border border-indigo-300 bg-white px-2 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                  >
                    <Wallet className="h-3.5 w-3.5" />
                    Mettre {euros(lecture.ecartCents)} en provision
                  </button>
                )}
              </div>
              {compteOuvert === userId && (
                <div className="mt-2">
                  {/*
                    La clé porte le reliquat : quand une écriture le change, ce
                    panneau se remonte et relit. Un compte figé sur des soldes
                    d'avant l'écriture ferait décider la suite sur du périmé.
                  */}
                  <CompteDeLaFamille
                    key={paiement.unallocatedCents}
                    userId={userId}
                    paiementExamine={paiement.id}
                    ibanExamine={paiement.counterpartyIban}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
